import React from 'react';
import { motion } from 'framer-motion';
import { Heart, Users, Target, Globe, ArrowRight, CheckCircle2 } from 'lucide-react';
import { useT } from '../components/site/i18n';
import { PhoneImage } from '../components/site/PhoneImage';
import { Link } from 'react-router-dom';
const FadeIn = ({
  children,
  delay = 0,
  className = ''
}: {
  children: React.ReactNode;
  delay?: number;
  className?: string;
}) => <motion.div initial={{
  opacity: 0,
  y: 30
}} whileInView={{
  opacity: 1,
  y: 0
}} viewport={{
  once: true,
  margin: '-50px'
}} transition={{
  duration: 0.7,
  delay,
  ease: [0.21, 0.47, 0.32, 0.98]
}} className={className} data-id="element-1014" data-mp="1zb3v4dxl5jjh">
    {children}
  </motion.div>;
export function ParaOng() {
  const t = useT();
  return <div className="min-h-screen bg-brand-cream dark:bg-gray-900 pt-24 pb-24 transition-colors duration-300" data-id="element-1015" data-mp="2i3pj6c2945ti">
      {/* Hero Section */}
      <section className="max-w-7xl mx-auto px-6 pt-12 pb-24" data-id="element-1016" data-mp="2dzhv8h3matwc">
        <FadeIn className="text-center max-w-4xl mx-auto" data-id="element-1017" data-mp="zbddqyh9nv80">
          <span className="text-brand-green-dark dark:text-brand-green-light font-bold tracking-wider uppercase text-sm mb-6 flex items-center justify-center gap-2" data-id="element-1018" data-mp="13aujzu4iowe8">
            <Heart size={16} className="text-brand-yellow" data-id="element-1019" data-mp="18ctppanqc5ec" />
            {t.paraOng.heroEyebrow}
          </span>
          <h1 className="text-5xl md:text-7xl font-sans font-extrabold text-brand-charcoal dark:text-white mb-6 tracking-tight" data-id="element-1020" data-mp="1i8mxyhuf1cqb">
            {t.paraOng.heroTitle1}{' '}
            <span className="relative inline-block" data-id="element-1021" data-mp="1m8fdjei88j56">
              <span className="relative z-10 font-display text-brand-green dark:text-brand-green-light font-normal text-6xl md:text-8xl tracking-normal transform -rotate-2 inline-block" data-id="element-1022" data-mp="1r0owgakxcixy">
                {t.paraOng.heroTitle2}
              </span>
              <svg className="absolute -bottom-2 left-0 w-full h-4 text-brand-yellow -z-10" viewBox="0 0 100 10" preserveAspectRatio="none" data-id="element-1023" data-mp="210z83qrb9ib0">
                <path d="M0 5 Q 50 10 100 5" stroke="currentColor" strokeWidth="4" fill="none" strokeLinecap="round" data-id="element-1024" data-mp="1g7zh7ztylup0" />
              </svg>
            </span>
          </h1>
          <p className="text-xl text-brand-charcoal/70 dark:text-gray-300 leading-relaxed mb-10" data-id="element-1025" data-mp="1l7lu285vl8xa">
            {t.paraOng.heroDesc}
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4" data-id="element-1026" data-mp="1u1iad6cczr4s">
            <Link to="/contato" className="bg-brand-green hover:bg-brand-green-dark text-white px-8 py-4 rounded-full font-bold transition-all shadow-sm hover:shadow-md flex items-center gap-2 w-full sm:w-auto justify-center" data-id="element-1027" data-mp="1u3uvtthbsjml">
              {t.paraOng.ctaButton} <ArrowRight size={18} data-id="element-1028" data-mp="emkx5k39xcd6" />
            </Link>
          </div>
        </FadeIn>
      </section>

      {/* What Section */}
      <section className="bg-white dark:bg-gray-800 py-24 transition-colors duration-300" data-id="element-1029" data-mp="2l8gvjxcepfhf">
        <div className="max-w-7xl mx-auto px-6" data-id="element-1030" data-mp="2q7trr8p63ywo">
          <FadeIn className="text-center max-w-3xl mx-auto mb-16" data-id="element-1031" data-mp="2q7k2xofuzxsk">
            <h2 className="text-3xl md:text-4xl font-extrabold text-brand-charcoal dark:text-white mb-6" data-id="element-1032" data-mp="2bjyy9q3c5qij">
              {t.paraOng.whatTitle}
            </h2>
            <p className="text-lg text-brand-charcoal/70 dark:text-gray-300" data-id="element-1033" data-mp="2gfxjj4tdaseq">
              {t.paraOng.whatDesc}
            </p>
          </FadeIn>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8" data-id="element-1034" data-mp="26l5jlucbbtgs">
            {[{
            title: t.paraOng.benefit1Title,
            desc: t.paraOng.benefit1Desc,
            icon: Users
          }, {
            title: t.paraOng.benefit2Title,
            desc: t.paraOng.benefit2Desc,
            icon: Target
          }, {
            title: t.paraOng.benefit3Title,
            desc: t.paraOng.benefit3Desc,
            icon: Heart
          }, {
            title: t.paraOng.benefit4Title,
            desc: t.paraOng.benefit4Desc,
            icon: Globe
          }].map((benefit, i) => <FadeIn key={i} delay={i * 0.1} className="bg-gray-50 dark:bg-gray-900 p-8 rounded-3xl border border-gray-100 dark:border-gray-700" data-id="element-1035" data-mp="1iwca27g6g0vs" data-mpi={i}>
                <div className="w-12 h-12 bg-brand-yellow/20 text-brand-yellow-dark dark:text-brand-yellow rounded-2xl flex items-center justify-center mb-6" data-id="element-1036" data-mp="145lkl6225dvu">
                  <benefit.icon size={24} data-id="element-1037" data-mp="z71tkr8lxbwm" />
                </div>
                <h3 className="text-xl font-bold text-brand-charcoal dark:text-white mb-3" data-id="element-1038" data-mp="v5q20g66agls">
                  {benefit.title}
                </h3>
                <p className="text-brand-charcoal/70 dark:text-gray-400 leading-relaxed" data-id="element-1039" data-mp="q52gub4mktzh">
                  {benefit.desc}
                </p>
              </FadeIn>)}
          </div>
        </div>
      </section>

      {/* BairrouImpacta! Section */}
      <section className="py-24 overflow-hidden" data-id="element-1040" data-mp="31s4hpdx7zyqg">
        <div className="max-w-7xl mx-auto px-6" data-id="element-1041" data-mp="31v0g13czcpff">
          <div className="grid lg:grid-cols-2 gap-16 items-center" data-id="element-1042" data-mp="36tbrtjmu5a0a">
            <FadeIn className="relative flex justify-center lg:justify-start order-2 lg:order-1" data-id="element-1043" data-mp="3atltik3lhe9c">
              <div className="absolute inset-0 bg-brand-yellow/10 dark:bg-brand-yellow/5 rounded-full blur-3xl -z-10 transform scale-125" data-id="element-1044" data-mp="2e3v2wvyfcb8t"></div>
              <PhoneImage src="https://cdn.magicpatterns.com/uploads/rBKDBrCYsC8iZEEEAuogN1/Impact.png" alt={t.paraOng.impactaMockAlt} className="transform -rotate-2 hover:rotate-0 transition-transform duration-500" data-id="element-1045" data-mp="2j3hbut77z5bt" />
            </FadeIn>
            <FadeIn delay={0.15} className="order-1 lg:order-2" data-id="element-1046" data-mp="1vclr2g16lae2">
              <span className="text-brand-green-dark dark:text-brand-green-light font-bold tracking-wider uppercase text-sm mb-4 flex items-center gap-2" data-id="element-1047" data-mp="1ze6wjgbdyme8">
                <Target size={16} className="text-brand-yellow" data-id="element-1048" data-mp="32njf7veg3fer" />
                {t.paraOng.impactaEyebrow}
              </span>
              <h2 className="text-3xl md:text-4xl font-extrabold text-brand-charcoal dark:text-white mb-6 tracking-tight" data-id="element-1049" data-mp="3cf5z0g4ummt4">
                {t.paraOng.impactaTitle}
              </h2>
              <p className="text-lg text-brand-charcoal/70 dark:text-gray-300 leading-relaxed mb-8" data-id="element-1050" data-mp="37ip3v1z8f86j">
                {t.paraOng.impactaDesc}
              </p>
              <ul className="space-y-4" data-id="element-1051" data-mp="2esytn40q0qfp">
                {[t.paraOng.impactaList1, t.paraOng.impactaList2, t.paraOng.impactaList3].map((item, i) => <li key={i} className="flex items-start gap-3 text-brand-charcoal/80 dark:text-gray-300" data-id="element-1052" data-mp="2orxxm9ajjxwd" data-mpi={i}>
                    <CheckCircle2 size={22} className="text-brand-green shrink-0 mt-0.5" data-id="element-1053" data-mp="3yug0mx9i0vp" />
                    <span className="font-medium" data-id="element-1054" data-mp="ghyf4gdt4j48">{item}</span>
                  </li>)}
              </ul>
            </FadeIn>
          </div>
        </div>
      </section>

      {/* Steps Section */}
      <section className="py-24" data-id="element-1055" data-mp="6jioeeiozbv9">
        <div className="max-w-7xl mx-auto px-6" data-id="element-1056" data-mp="6kkb5g92lmst">
          <FadeIn className="text-center mb-16" data-id="element-1057" data-mp="u9wh8793peqw">
            <h2 className="text-3xl md:text-4xl font-extrabold text-brand-charcoal dark:text-white mb-6" data-id="element-1058" data-mp="ua4w24osau4q">
              {t.paraOng.stepsTitle}
            </h2>
          </FadeIn>

          <div className="grid md:grid-cols-3 gap-8 relative" data-id="element-1059" data-mp="kis3tbz2q1y6">
            <div className="hidden md:block absolute top-12 left-1/6 right-1/6 h-0.5 bg-gray-200 dark:bg-gray-700 -z-10" data-id="element-1060" data-mp="piwkb1zzewoe"></div>
            {[{
            title: t.paraOng.step1Title,
            desc: t.paraOng.step1Desc,
            num: '1'
          }, {
            title: t.paraOng.step2Title,
            desc: t.paraOng.step2Desc,
            num: '2'
          }, {
            title: t.paraOng.step3Title,
            desc: t.paraOng.step3Desc,
            num: '3'
          }].map((step, i) => <FadeIn key={i} delay={i * 0.1} className="text-center relative" data-id="element-1061" data-mp="30bgxtkbogu7s" data-mpi={i}>
                <div className="w-24 h-24 mx-auto bg-white dark:bg-gray-800 border-4 border-brand-cream dark:border-gray-900 rounded-full flex items-center justify-center shadow-sm mb-6 relative z-10" data-id="element-1062" data-mp="vtvyuos6orh">
                  <span className="text-3xl font-display text-brand-green dark:text-brand-green-light" data-id="element-1063" data-mp="3o09mqwtvu1sw">
                    {step.num}
                  </span>
                </div>
                <h3 className="text-xl font-bold text-brand-charcoal dark:text-white mb-3" data-id="element-1064" data-mp="3j3sym2monnft">
                  {step.title}
                </h3>
                <p className="text-brand-charcoal/70 dark:text-gray-400" data-id="element-1065" data-mp="27m07qtbj5peq">
                  {step.desc}
                </p>
              </FadeIn>)}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="max-w-5xl mx-auto px-6 pb-12" data-id="element-1066" data-mp="22hpgf790xeos">
        <FadeIn className="bg-brand-green dark:bg-brand-green-dark rounded-[3rem] p-12 md:p-20 text-center relative overflow-hidden" data-id="element-1067" data-mp="27fzfvu1s71g7">
          <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" data-id="element-1068" data-mp="2c8sf62ehdhkw"></div>
          <div className="absolute bottom-0 left-0 w-64 h-64 bg-brand-yellow/20 rounded-full blur-3xl translate-y-1/2 -translate-x-1/2" data-id="element-1069" data-mp="2gb72ozt3h5ii"></div>

          <div className="relative z-10" data-id="element-1070" data-mp="2q81wx6ic4jqz">
            <h2 className="text-4xl md:text-5xl font-extrabold text-white mb-6" data-id="element-1071" data-mp="2q4xjdiizcbnn">
              {t.paraOng.ctaTitle}
            </h2>
            <p className="text-xl text-white/90 mb-10 max-w-2xl mx-auto" data-id="element-1072" data-mp="vtiut51pgi9h">
              {t.paraOng.ctaDesc}
            </p>
            <Link to="/contato" className="inline-flex items-center gap-2 bg-brand-yellow hover:bg-brand-yellow-dark text-brand-charcoal px-8 py-4 rounded-full font-bold transition-all shadow-sm hover:shadow-md" data-id="element-1073" data-mp="10t3szd91wml5">
              {t.paraOng.ctaButton} <ArrowRight size={18} data-id="element-1074" data-mp="vh7b3a5vgkrs" />
            </Link>
          </div>
        </FadeIn>
      </section>
    </div>;
}